<template>
  <nav class="pagination-controls" aria-label="pagination">
    <!-- Prev Page Button -->
    <div
      v-if="showPreviousPage"
      class="pagination-controls__nav-container"
    >
      <router-link
        ref="prev-button"
        class="pagination-controls__nav-button"
        :to="buildLink(previousPage)"
      >
        <icon-component
          name="caret"
          class="
            pagination-controls__button-icon
            pagination-controls__button-icon--prev
            icon--smallest
          "
        />
        <span class="pagination-controls__button-text">
          Prev
        </span>
        <span class="visually-hidden">page</span>
      </router-link>
    </div>
    <!-- First Page Number -->
    <router-link
      v-if="showFirstPage"
      class="pagination-controls__number"
      :to="buildLink(1)"
    >
      <span class="visually-hidden">page</span>
      1
    </router-link>
    <!-- ellipsis -->
    <span v-if="showPreviousDots" class="pagination-controls__dots">...</span>
    <!-- Two Pages Back Number -->
    <router-link
      v-if="showTwoPagesPreviousToCurrentPage"
      class="pagination-controls__number"
      :to="buildLink(twoPagesPreviousToCurrentPage)"
    >
      <span class="visually-hidden">page</span>
      {{ twoPagesPreviousToCurrentPage }}
    </router-link>
    <!-- Previous Page Number -->
    <router-link
      v-if="showPreviousPage"
      class="pagination-controls__number"
      :to="buildLink(previousPage)"
    >
      <span class="visually-hidden">page</span>
      {{ previousPage }}
    </router-link>
    <!-- Current Page Number -->
    <button
      class="
        pagination-controls__current-page-number
        button-reset
      "
      disabled="true"
      aria-current="page"
      aria-disabled="true"
    >
      <span class="visually-hidden">page</span>
      {{ currentPage }}
    </button>
    <!-- Next Page Number -->
    <router-link
      v-if="showNextPage"
      class="pagination-controls__number"
      :to="buildLink(nextPage)"
    >
      <span class="visually-hidden">page</span>
      {{ nextPage }}
    </router-link>
    <!-- Two Pages Forward Number -->
    <router-link
      v-if="showTwoPagesAfterCurrentPage"
      class="pagination-controls__number"
      :to="buildLink(twoPagesAfterCurrentPage)"
    >
      <span class="visually-hidden">page</span>
      {{ twoPagesAfterCurrentPage }}
    </router-link>
    <!-- ellipsis -->
    <span v-if="showNextDots" class="pagination-controls__dots">...</span>
    <!-- Last Page Number -->
    <router-link
      v-if="showLastPage"
      class="pagination-controls__number"
      :to="buildLink(totalPages)"
    >
      <span class="visually-hidden">page</span>
      {{ totalPages }}
    </router-link>
    <div
      v-if="showNextPage"
      class="pagination-controls__nav-container"
    >
      <!-- Next Page Button -->
      <router-link
        ref="next-button"
        class="pagination-controls__nav-button"
        :to="buildLink(nextPage)"
      >
        <span class="pagination-controls__button-text">
          Next
        </span>
        <icon-component
          name="caret"
          class="
            pagination-controls__button-icon
            pagination-controls__button-icon--next
            icon--smallest
          "
        />
        <span class="visually-hidden">page</span>
      </router-link>
    </div>
  </nav>
</template>

<script>
import { isDesktop } from '@/utils/viewport-width-helpers';

export default {
  name: 'PaginationControls',
  props: {
    totalPages: {
      type: Number,
      required: true,
    },
    currentPage: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      default: 20,
    },
  },
  data() {
    return {
      isDesktop: false,
    };
  },
  computed: {
    previousPage() {
      return this.currentPage - 1;
    },
    twoPagesPreviousToCurrentPage() {
      return this.currentPage - 2;
    },
    nextPage() {
      return this.currentPage + 1;
    },
    twoPagesAfterCurrentPage() {
      return this.currentPage + 2;
    },
    showPreviousPage() {
      return this.currentPage > 1;
    },
    showTwoPagesPreviousToCurrentPage() {
      if (this.isDesktop) {
        return this.currentPage > 2;
      }
      return this.currentPage === this.totalPages && this.totalPages > 3;
    },
    showFirstPage() {
      if (this.isDesktop) {
        return this.currentPage > 3;
      }
      return this.currentPage > 2;
    },
    showPreviousDots() {
      if (this.isDesktop) {
        return this.currentPage > 4;
      }
      return this.currentPage > 3;
    },
    showNextPage() {
      return this.currentPage < this.totalPages;
    },
    showTwoPagesAfterCurrentPage() {
      if (this.isDesktop) {
        return this.nextPage < this.totalPages;
      }
      return this.currentPage === 1 && this.totalPages > 3;
    },
    showLastPage() {
      if (this.isDesktop) {
        return this.twoPagesAfterCurrentPage < this.totalPages;
      }
      return this.nextPage < this.totalPages;
    },
    showNextDots() {
      if (this.isDesktop) {
        return this.twoPagesAfterCurrentPage + 1 < this.totalPages;
      }
      return this.twoPagesAfterCurrentPage < this.totalPages;
    },
  },
  mounted() {
    this.isDesktop = isDesktop();
  },
  methods: {
    buildLink(pageNumber) {
      // pagination based on route params e.g. /page/1
      if (this.$route.params.pageNumber) {
        return {
          name: this.$route.name,
          params: {
            pageNumber,
          },
        };
      }
      // pagination based on route query string e.g. ?page=1
      return {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          page: pageNumber,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_pagination-controls';
</style>

<docs>

The PaginationControls component is used to paginate an array of content using
vue-router and the url param ?page as it's data store. This levarages native
browser functionality and allows users to navigate back through history with no
extra complexity.
</docs>
